import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ApiService } from '../services/api';
import { AuthService } from '../services/auth';
import { Question } from '../types';
import { marked } from 'marked';

interface QuestionFormData {
  title: string;
  content: string;
  type: 'multiple_choice' | 'open_ended' | 'true_false' | 'rating' | 'investigation';
  options: Array<{
    text: string;
    explication: string;
  }>;
  correctAnswer: string;
  ratingScale: {
    min: number;
    max: number;
    labels: string[];
  };
  timeLimit: number;
  proposition: string;
}

const EditQuestion: React.FC = () => {
  const { sessionId, questionId } = useParams<{ sessionId: string; questionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teacher, setTeacher] = useState<any>(null);
  const [question, setQuestion] = useState<Question | null>(null);
  const [formData, setFormData] = useState<QuestionFormData>({
    title: '',
    content: '',
    type: 'multiple_choice',
    options: [
      { text: '', explication: '' },
      { text: '', explication: '' }
    ],
    correctAnswer: '',
    ratingScale: {
      min: 1,
      max: 5,
      labels: ['Très insatisfait', 'Insatisfait', 'Neutre', 'Satisfait', 'Très satisfait']
    },
    timeLimit: 60,
    proposition: ''
  });

  const loadQuestion = useCallback(async () => {
    if (!questionId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await ApiService.getQuestion(questionId);
      
      if (response.success) {
        const q = response.question;
        setQuestion(q);
        
        // Transformer les options pour le formulaire
        const options = q.options?.map((opt: any) => ({
          text: opt.text || opt,
          explication: opt.explication || ''
        })) || [
          { text: '', explication: '' },
          { text: '', explication: '' }
        ];
        
        setFormData({
          title: q.title,
          content: q.content,
          type: q.type,
          options: options.length > 0 ? options : ['', ''],
          correctAnswer: q.correctAnswer || '',
          ratingScale: q.ratingScale || {
            min: 1,
            max: 5,
            labels: ['Très insatisfait', 'Insatisfait', 'Neutre', 'Satisfait', 'Très satisfait']
          },
          timeLimit: q.timeLimit || 60,
          proposition: q.proposition || ''
        });
      } else {
        setError('Erreur lors du chargement de la question');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement de la question');
    } finally {
      setLoading(false);
    }
  }, [questionId]);

  useEffect(() => {
    // Vérifier si un enseignant est connecté
    const currentTeacher = AuthService.getTeacher();
    if (!currentTeacher) {
      navigate('/teacher/login');
      return;
    }
    
    setTeacher(currentTeacher);
    
    if (sessionId && questionId) {
      loadQuestion();
    }
  }, [navigate, sessionId, questionId, loadQuestion]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'type') {
      const newType = value as QuestionFormData['type'];
      setFormData(prev => ({
        ...prev,
        type: newType,
        correctAnswer: (newType === 'open_ended' || newType === 'rating' || newType === 'investigation') ? '' : prev.correctAnswer
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], text: value };
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  const handleExplicationChange = (index: number, value: string) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], explication: value };
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { text: '', explication: '' }]
    }));
  };

  const removeOption = (index: number) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        options: newOptions,
        correctAnswer: prev.correctAnswer === index.toString() ? '' : prev.correctAnswer
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId || !questionId) {
      setError('ID de session ou de question manquant');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Préparer les données de la question
      const questionData = {
        title: formData.title,
        content: formData.content,
        type: formData.type,
        timeLimit: formData.timeLimit,
        options: formData.type === 'open_ended' || formData.type === 'rating' ? [] : formData.options.filter(opt => opt.text.trim() !== '').map((opt, index) => ({
          id: index.toString(),
          text: opt.text,
          explication: opt.explication
        })),
        correctAnswer: (formData.type === 'open_ended' || formData.type === 'rating' || formData.type === 'investigation') ? '' : formData.correctAnswer,
        ratingScale: formData.type === 'rating' ? formData.ratingScale : undefined,
        proposition: formData.type === 'open_ended' ? formData.proposition : undefined
      };

      const response = await ApiService.updateQuestion(questionId, questionData);

      if (response.success) {
        // Rediriger vers la page de gestion de session
        navigate(`/teacher/sessions/${sessionId}`);
      } else {
        setError('Erreur lors de la mise à jour de la question');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour de la question');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    AuthService.logoutTeacher();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Édition de Question</h1>
            <div className="header-actions">
              {teacher && (
                <div className="teacher-info">
                  <span className="teacher-name">
                    <i className="fas fa-user"></i>
                    {teacher.name}
                  </span>
                  <span className="teacher-stats">
                    {teacher.totalSessions} sessions • {teacher.totalStudents} étudiants
                  </span>
                </div>
              )}
              <button onClick={handleLogout} className="btn btn-outline btn-sm">
                <i className="fas fa-sign-out-alt"></i>
                Déconnexion
              </button>
              <Link to={`/teacher/sessions/${sessionId}`} className="btn btn-outline btn-sm">
                ← Retour à la session
              </Link>
            </div>
          </div>
        </header>
        <main className="main-content">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </main>
      </div>
    );
  }

  if (error && !question) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Édition de Question</h1>
            <div className="header-actions">
              <Link to={`/teacher/sessions/${sessionId}`} className="btn btn-outline btn-sm">
                ← Retour à la session
              </Link>
            </div>
          </div>
        </header>
        <main className="main-content">
          <div className="card">
            <div className="card-body text-center">
              <div className="error-message">
                <i className="fas fa-exclamation-triangle"></i>
                <span>{error}</span>
              </div>
              <button onClick={() => navigate(`/teacher/sessions/${sessionId}`)} className="btn btn-primary">
                Retour à la session
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-content">
          <h1 className="logo">Édition de Question</h1>
          <div className="header-actions">
            {teacher && (
              <div className="teacher-info">
                <span className="teacher-name">
                  <i className="fas fa-user"></i>
                  {teacher.name}
                </span>
                <span className="teacher-stats">
                  {teacher.totalSessions} sessions • {teacher.totalStudents} étudiants
                </span>
              </div>
            )}
            <button onClick={handleLogout} className="btn btn-outline btn-sm">
              <i className="fas fa-sign-out-alt"></i>
              Déconnexion
            </button>
            <Link to={`/teacher/sessions/${sessionId}`} className="btn btn-outline btn-sm">
              ← Retour à la session
            </Link>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Modifier la question</h2>
          </div>
          
          <div className="card-body">
            <form onSubmit={handleSubmit} className="question-form">
              <div className="form-group">
                <label htmlFor="title" className="form-label">
                  Titre de la question *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Ex: Question sur les mathématiques"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="content" className="form-label">
                  Contenu de la question *
                </label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  className="form-input"
                  rows={4}
                  placeholder="Ex: Quelle est la dérivée de x² ?"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="type" className="form-label">
                  Type de question *
                </label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                >
                  <option value="multiple_choice">QCM (Choix multiple)</option>
                  <option value="open_ended">Question ouverte</option>
                  <option value="true_false">Vrai/Faux</option>
                  <option value="rating">Évaluation/Note</option>
                  <option value="investigation">Investigation (QCM sans réponse correcte)</option>
                </select>
              </div>

              {formData.type !== 'open_ended' && formData.type !== 'rating' && (
                <div className="form-group">
                  <label className="form-label">Options de réponse *</label>
                  {formData.options.map((option, index) => (
                    <div key={index} className="option-input-group">
                      <div className="option-main">
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => handleOptionChange(index, e.target.value)}
                          className="form-input"
                          placeholder={`Option ${index + 1}`}
                          required
                        />
                        <div className="option-actions">
                          {formData.type !== 'investigation' && (
                            <>
                              <input
                                type="radio"
                                name="correctAnswer"
                                value={index.toString()}
                                checked={formData.correctAnswer === index.toString()}
                                onChange={handleInputChange}
                                className="correct-answer-radio"
                              />
                              <label className="correct-answer-label">Correcte</label>
                            </>
                          )}
                          {formData.options.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeOption(index)}
                              className="btn btn-danger btn-sm"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="option-explication">
                        <label className="form-label-small">
                          <i className="fas fa-info-circle"></i>
                          Explication (optionnelle)
                        </label>
                        <textarea
                          value={option.explication}
                          onChange={(e) => handleExplicationChange(index, e.target.value)}
                          className="form-input explication-textarea"
                          placeholder="Expliquez pourquoi cette réponse est correcte ou incorrecte..."
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addOption}
                    className="btn btn-outline btn-sm"
                  >
                    <i className="fas fa-plus"></i> Ajouter une option
                  </button>
                </div>
              )}

              {formData.type === 'rating' && (
                <div className="form-group">
                  <label className="form-label">Configuration de l'échelle *</label>
                  
                  <div className="rating-config">
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Valeur minimale</label>
                        <input
                          type="number"
                          name="ratingMin"
                          value={formData.ratingScale.min}
                          onChange={(e) => setFormData({
                            ...formData,
                            ratingScale: {
                              ...formData.ratingScale,
                              min: parseInt(e.target.value) || 1
                            }
                          })}
                          className="form-input"
                          min="1"
                          max="10"
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Valeur maximale</label>
                        <input
                          type="number"
                          name="ratingMax"
                          value={formData.ratingScale.max}
                          onChange={(e) => setFormData({
                            ...formData,
                            ratingScale: {
                              ...formData.ratingScale,
                              max: parseInt(e.target.value) || 5
                            }
                          })}
                          className="form-input"
                          min="2"
                          max="10"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Labels (optionnel)</label>
                      <small className="form-help">
                        Laissez vide pour utiliser les valeurs numériques, ou définissez des labels personnalisés
                      </small>
                      {Array.from({ length: formData.ratingScale.max - formData.ratingScale.min + 1 }, (_, index) => {
                        const value = formData.ratingScale.min + index;
                        return (
                          <div key={value} className="rating-label-input">
                            <label className="form-label" style={{ minWidth: '60px' }}>
                              {value}:
                            </label>
                            <input
                              type="text"
                              value={formData.ratingScale.labels[index] || ''}
                              onChange={(e) => {
                                const newLabels = [...formData.ratingScale.labels];
                                newLabels[index] = e.target.value;
                                setFormData({
                                  ...formData,
                                  ratingScale: {
                                    ...formData.ratingScale,
                                    labels: newLabels
                                  }
                                });
                              }}
                              className="form-input"
                              placeholder={`Label pour ${value}`}
                              style={{ flex: 1 }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {formData.type === 'open_ended' && (
                <div className="form-group">
                  <label htmlFor="proposition" className="form-label">
                    Proposition de réponse (optionnel)
                  </label>
                  <small className="form-help">
                    Cette proposition sera affichée aux étudiants après qu'ils aient soumis leur réponse pour les aider à améliorer leur compréhension.
                  </small>
                  <textarea
                    id="proposition"
                    name="proposition"
                    value={formData.proposition}
                    onChange={handleInputChange}
                    className="form-input"
                    rows={6}
                    placeholder="Saisissez une proposition de réponse détaillée qui aidera les étudiants à mieux comprendre le sujet..."
                  />
                  <small className="form-help">
                    💡 Vous pouvez utiliser le formatage Markdown : **texte en gras**, *texte en italique*, - listes à puces
                  </small>
                  
                  {formData.proposition && (
                    <div className="mt-2 p-3" style={{
                      background: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}>
                      <strong>Aperçu du rendu :</strong>
                      <div 
                        className="proposition-preview"
                        style={{ 
                          marginTop: '0.5rem',
                          lineHeight: '1.5'
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: marked.parse(formData.proposition, {
                            breaks: true,
                            gfm: true
                          }) as string
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="timeLimit" className="form-label">
                  Temps limite (secondes) *
                </label>
                <input
                  type="number"
                  id="timeLimit"
                  name="timeLimit"
                  value={formData.timeLimit}
                  onChange={handleInputChange}
                  className="form-input"
                  min="10"
                  max="300"
                  required
                />
              </div>

              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>{error}</span>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => navigate(`/teacher/sessions/${sessionId}`)}
                  className="btn btn-outline"
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="button-spinner"></div>
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Sauvegarder
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EditQuestion;
